import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import PropTypes from 'prop-types';
import { stringify } from 'query-string';
import { options } from 'config/reactRedux';
import {
  getSearchPath,
  getSearchParams,
} from 'selectors/searchSelectors';
import createGetTranslation from 'selectors/i18n';
import icons from 'config/icons';
import InputText from 'components/form/inputText';
import withStyles from 'isomorphic-style-loader/withStyles';
import I18n from 'components/i18n';
import { ToggleContainerContext } from 'context/toggleContainer';

import styles from './searchForm.css';

const SearchForm = ({ params, search, getTranslation }) => {
  const [searchParam, setSearchParam] = useState(params.s);
  const searchInputElement = useRef();
  const { close: closeContainer, isOpened } = useContext(ToggleContainerContext);
  const previouslyOpened = useRef();

  const onSubmit = useCallback((e) => {
    e.preventDefault();

    // Hide search overlay, if it's open
    closeContainer();

    // Auto-hide mobile, software keyboards
    const blurElement = e.currentTarget[0];
    blurElement.focus();
    blurElement.blur();
    const searchTerm = searchParam && searchParam.trim();
    if (searchTerm) {
      search({ s: searchTerm });
    }
  }, [searchParam, search]);

  const closeContainerOnEsc = useCallback((e) => {
    if ('Esc' === e.key || 'Escape' === e.key) {
      closeContainer();
    }
  }, [closeContainer]);

  useEffect(() => {
    if (false === previouslyOpened.current && isOpened) {
      searchInputElement.current.focus();
    }
    previouslyOpened.current = isOpened;
  }, [isOpened]);

  useEffect(() => {
    setSearchParam(params.s);
  }, [params.s]);

  return (
    <div className={styles.wrapper} id="search-form">
      <form action="/" onSubmit={onSubmit}>
        <div className={styles.inner}>
          <a>
            <button className={styles.button} type="submit">
              <img
                alt="Search Icon"
                src={icons.search}
                height="18"
                width="18"
              />
            </button>
          </a>
          <InputText
            name="search"
            type="search"
            inputRef={searchInputElement}
            placeholder={
              getTranslation(
                'search-bar',
                'Search Babbel Magazine',
              )
            }
            className={styles.input}
            value={searchParam}
            onChange={(e) => setSearchParam(e.target.value)}
            onKeyDown={closeContainerOnEsc}
            label="Search"
            shouldHideLabel
          />
          <a>
            <button className={styles.lgScreenSubmit} type="submit">
              <I18n
                field="search"
                defaultText="Search"
              />
            </button>
          </a>
        </div>
      </form>
    </div>
  );
};

SearchForm.propTypes = {
  getTranslation: PropTypes.func.isRequired,
  params: PropTypes.object.isRequired,
  search: PropTypes.func.isRequired,
};

export const mapStateToProps = (state) => ({
  getTranslation: createGetTranslation(state),
  searchPath: getSearchPath(state),
  params: getSearchParams(state),
});

export const mapDispatchToProps = (dispatch) => ({
  search: (searchPath) => (params) => {
    const queryString = stringify(params, { arrayFormat: 'bracket' });
    dispatch(push(`${searchPath}?${queryString}`));
  },
});

export const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...stateProps,
  ...ownProps,
  search: dispatchProps.search(stateProps.searchPath),
});

const withRedux = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
  options,
);

export default withRedux(withStyles(styles)(SearchForm));
